// TODO: This file cannot be imported in web workers due to window.navigator dependency
// Consider refactoring to separate platform detection from keyboard utilities
import type { PointerLikeEvent } from './mouse';
import { MouseButton } from './mouse';

export const IS_MAC = !!window.navigator.platform.match(/^Mac/);

export const Modifier = {
  CTRL: IS_MAC ? '⌘' : 'Ctrl',
  SHIFT: IS_MAC ? '⇧' : 'Shift',
  ALT: IS_MAC ? '⌥' : 'Alt',
  CLICK: 'Click',
};

// Used with KeyboardEvent.key because KeyboardEvent.keyCode is deprecated.
export const KEY = {
  ESCAPE: 'Escape',
  ENTER: 'Enter',
  CONTROL: 'Control',
  SHIFT: 'Shift',
  ARROW_UP: 'ArrowUp',
  ARROW_DOWN: 'ArrowDown',
  ARROW_LEFT: 'ArrowLeft',
  ARROW_RIGHT: 'ArrowRight',
  BACKSPACE: 'Backspace',
  DELETE: 'Delete',
};

export function ctrlOrCmdKeyPressed(
  event: MouseEvent | React.MouseEvent | KeyboardEvent | React.KeyboardEvent,
): boolean {
  return IS_MAC ? event.metaKey : event.ctrlKey;
}

/**
 * Returns true when Ctrl or Meta/Cmd is pressed, regardless of platform.
 * Unlike ctrlOrCmdKeyPressed, this treats both keys as active on all platforms,
 * so Ctrl still works on Mac (e.g. for modifier-gesture detection).
 */
export function ctrlOrMetaKeyPressed(e: Readonly<{ ctrlKey: boolean; metaKey: boolean }>): boolean {
  return e.ctrlKey || e.metaKey;
}

export function shiftKeyPressed(
  event: MouseEvent | React.MouseEvent | KeyboardEvent | React.KeyboardEvent,
): boolean {
  return event.shiftKey;
}

export function altOrOptionKeyPressed(
  event: MouseEvent | React.MouseEvent | KeyboardEvent | React.KeyboardEvent,
): boolean {
  return event.altKey;
}

/**
 * Checks if any modifier key (Alt, Shift, Ctrl, or Meta/Cmd) is pressed for the given event.
 *
 * @param event - The keyboard or pointer event to check
 * @returns `true` if Alt, Shift, Ctrl, or Meta/Cmd is pressed; `false` otherwise
 */
export function isAnyModifierKeyPressed(event: PointerLikeEvent | KeyboardEvent): boolean {
  const metaKeyPressed = 'metaKey' in event && event.metaKey;
  return (
    altOrOptionKeyPressed(event) ||
    shiftKeyPressed(event) ||
    ctrlOrCmdKeyPressed(event) ||
    metaKeyPressed
  );
}

/**
 * Checks for a plain primary-button click: no Ctrl, Meta/Cmd, Shift, or Alt held,
 * reading the raw keys identically on every platform (unlike isAnyModifierKeyPressed,
 * which maps Ctrl to Cmd on Mac). Use it to decide whether to intercept a link click:
 * any modified or non-primary click keeps its native behavior (new tab or window,
 * context menu, download, text selection).
 */
export function isPlainPrimaryClick(event: PointerLikeEvent): boolean {
  return (
    event.button === MouseButton.PRIMARY &&
    !event.ctrlKey &&
    !event.metaKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

/**
 * Checks if the event matches the required modifier state.
 *
 * This function implements the core modifier matching logic:
 * - If a modifier is required (e.g., `altModifier: true`), that modifier must be pressed
 * - If a modifier is not required (undefined), that modifier must NOT be pressed
 * - All required modifiers must match exactly for the function to return true
 *
 * Examples:
 * - `{ altModifier: true }` matches when Alt is pressed and Shift/Ctrl are not
 * - `{ altModifier: true, shiftModifier: true }` matches when both Alt and Shift are pressed
 * - `{}` (no modifiers) matches when no modifiers are pressed
 *
 * @param requiredModifiers - The required modifier state with optional modifier fields
 * @param event - The keyboard or pointer event to check against the required modifiers
 * @returns `true` if the event's modifier state exactly matches the required modifiers; `false` otherwise
 */
export function matchesModifiers(
  requiredModifiers: {
    altModifier?: boolean;
    shiftModifier?: boolean;
    ctrlOrCmdModifier?: boolean;
  },
  event: PointerLikeEvent | KeyboardEvent,
): boolean {
  // Check both ctrlKey and metaKey to catch all Ctrl-like modifiers on Mac.
  // Unlike ctrlOrCmdKeyPressed (which only checks metaKey on Mac), this also detects the actual Ctrl key.
  // Note: mouse/pointer events don't have metaKey, but accessing it returns undefined (falsy), which is safe.
  const hasCtrl = Boolean((event as KeyboardEvent).ctrlKey || (event as KeyboardEvent).metaKey);

  // Modifiers must match exactly: if specified, must be pressed; if not specified, must not be pressed.
  const matchesAlt = (requiredModifiers.altModifier === true) === altOrOptionKeyPressed(event);
  const matchesShift = (requiredModifiers.shiftModifier === true) === shiftKeyPressed(event);
  const matchesCtrl = (requiredModifiers.ctrlOrCmdModifier === true) === hasCtrl;

  return matchesAlt && matchesShift && matchesCtrl;
}

export const KEYCODE = {
  BACKSPACE: 8,
  ENTER: 13,
  SHIFT: 16,
  CTRL: 17,
  ESC: 27,
  SPACE: 32,
  LEFT_ARROW: 37,
  UP_ARROW: 38,
  RIGHT_ARROW: 39,
  DOWN_ARROW: 40,
  SQUARE_BRACKET_LEFT: 219,
  SQUARE_BRACKET_RIGHT: 221,
  ONE: 49,
  TWO: 50,
  THREE: 51,
  FOUR: 52,
  FIVE: 53,
  SIX: 54,
  SEVEN: 55,
  A: 65,
  B: 66,
  C: 67,
  D: 68,
  E: 69,
  F: 70,
  G: 71,
  H: 72,
  I: 73,
  J: 74,
  K: 75,
  L: 76,
  M: 77,
  N: 78,
  O: 79,
  P: 80,
  Q: 81,
  R: 82,
  S: 83,
  T: 84,
  U: 85,
  V: 86,
  W: 87,
  Y: 89,
  Z: 90,
  SLASH: 191,
  BACKSLASH: 220,
  COMMA: 188,
  PERIOD: 190,
};

const KEYSTRING = {
  13: 'Enter',
  17: 'Control',
  27: 'Esc',
  32: 'Space',
  37: 'Left Arrow',
  38: 'Up Arrow',
  39: 'Right Arrow',
  40: 'Down Arrow',
  188: ',',
  190: '.',
  191: '/',
  220: '\\',
};

export function keyCodeToString(keyCode: number): string {
  return (
    (KEYSTRING as any)[keyCode] ||
    String.fromCharCode(keyCode >= 96 && keyCode <= 105 ? keyCode - 48 : keyCode)
  );
}

export interface KeyboardShortcut {
  keyCode: number;
  description: string;
  ctrlModifier?: boolean;
  shiftModifier?: boolean;
  altModifier?: boolean;
  clickModifier?: boolean;
  onAction?: (event: KeyboardEvent) => void;
  isCustom: boolean;
  // Customer facing action identifier string, allows customization
  actionId?: string;
  keyString?: string;
  preventDefaultAndStopPropagation?: boolean;
  allowInModal?: boolean;
}
